export type BooleanStateMap<TKey extends string> = Partial<Record<TKey, boolean>>;

/**
 * Base for singleton, localStorage-backed stores of boolean flags keyed by a string union.
 *
 * Handles the shared read/write lifecycle: an in-memory cache, lazy hydration from
 * localStorage (parse → validate → fall back to an empty map), skip-if-unchanged writes,
 * and clearing. Subclasses provide the storage key and a validator, and may hook into
 * save/clear (e.g. for cross-tab broadcasting).
 */
export abstract class BooleanStateStorage<TKey extends string> {
  protected cache: BooleanStateMap<TKey> | null = null;

  protected abstract readonly storageKey: string;

  protected abstract isValidState(value: unknown): value is BooleanStateMap<TKey>;

  get(key: TKey): boolean | undefined {
    return this.loadState()[key];
  }

  set(key: TKey, value: boolean): void {
    const state = this.loadState();

    if (state[key] === value) {
      return;
    }

    this.saveState({
      ...state,
      [key]: value,
    });
  }

  protected loadState(): BooleanStateMap<TKey> {
    if (this.cache !== null) {
      return this.cache;
    }

    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return (this.cache = {});
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return (this.cache = this.isValidState(parsed) ? parsed : {});
    } catch {
      return (this.cache = {});
    }
  }

  protected saveState(state: BooleanStateMap<TKey> | null): void {
    this.cache = state;

    if (state) {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
      this.onStateSaved(state);
    } else {
      localStorage.removeItem(this.storageKey);
      this.onStateCleared();
    }
  }

  protected onStateSaved(_state: BooleanStateMap<TKey>): void {
    // Optional hook for subclasses (e.g. cross-tab broadcast). No-op by default.
  }

  protected onStateCleared(): void {
    // Optional hook for subclasses (e.g. cross-tab broadcast). No-op by default.
  }
}
